/**
 * MQTT 连接层：单一 MqttClient + 全局消息路由 + 消息日志。
 *
 * - connect() 手动控制连接（不自动重连），参数持久化到 localStorage
 * - subscribeTopic(pattern, handler)：多组件可订阅同一/重叠主题，
 *   首个注册时真正 SUBSCRIBE，最后一个注销时 UNSUBSCRIBE，重连后自动恢复
 * - on_message 统一写日志（可按子串屏蔽高频主题）并分发到匹配的 handler
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import mqtt from "mqtt";

export type ConnState = "disconnected" | "connecting" | "connected";

export interface LogEntry {
  id: number;
  ts: number;
  dir: "in" | "out" | "sys";
  topic: string;
  preview: string;
  size: number;
}

export type MsgHandler = (topic: string, payload: string, bytes: Uint8Array) => void;

const LOG_CAP = 300;
/** 默认屏蔽的高频/大流量主题（子串匹配），避免刷爆日志 */
const DEFAULT_MUTED = ["/frame", "/model/chunk"];

/** 连接设置：一个主机地址 + 两个端口（WS、WebRTC），WS/WHEP 地址由此派生 */
export interface ConnSettings {
  host: string;
  wsPort: string;
  whepPort: string;
  username: string;
  password: string;
}

export const DEFAULT_SETTINGS: ConnSettings = {
  host: "127.0.0.1",
  wsPort: "9001",
  whepPort: "8889",
  username: "",
  password: "",
};

/** MQTT over WebSocket 连接地址 */
export function buildWsUrl(s: ConnSettings): string {
  return `ws://${s.host}:${s.wsPort}`;
}

/** WebRTC(WHEP) 拉流地址 */
export function buildWhepUrl(s: ConnSettings): string {
  return `http://${s.host}:${s.whepPort}/robot/cam1/whep`;
}

export function loadSettings(): ConnSettings {
  try {
    const s = JSON.parse(localStorage.getItem("mqtt-settings") ?? "");
    return {
      host: typeof s?.host === "string" ? s.host : DEFAULT_SETTINGS.host,
      wsPort: typeof s?.wsPort === "string" ? s.wsPort : DEFAULT_SETTINGS.wsPort,
      whepPort: typeof s?.whepPort === "string" ? s.whepPort : DEFAULT_SETTINGS.whepPort,
      username: typeof s?.username === "string" ? s.username : "",
      password: typeof s?.password === "string" ? s.password : "",
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: ConnSettings) {
  localStorage.setItem("mqtt-settings", JSON.stringify(s));
}

/** MQTT 通配符匹配：支持 + 和 # */
export function topicMatches(pattern: string, topic: string): boolean {
  const p = pattern.split("/");
  const t = topic.split("/");
  for (let i = 0; i < p.length; i++) {
    if (p[i] === "#") return true;
    if (i >= t.length || (p[i] !== "+" && p[i] !== t[i])) return false;
  }
  return p.length === t.length;
}

interface MqttContextValue {
  connState: ConnState;
  error: string | null;
  settings: ConnSettings;
  setSettings: Dispatch<SetStateAction<ConnSettings>>;
  connect: () => void;
  disconnect: () => void;
  subscribeTopic: (pattern: string, handler: MsgHandler) => () => void;
  publishMsg: (topic: string, payload: string, qos?: 0 | 1 | 2, retain?: boolean) => boolean;
  log: LogEntry[];
  clearLog: () => void;
  muted: string[];
  toggleMute: (sub: string) => void;
}

const MqttContext = createContext<MqttContextValue | null>(null);

export function useMqtt(): MqttContextValue {
  const ctx = useContext(MqttContext);
  if (!ctx) throw new Error("useMqtt must be used within MqttProvider");
  return ctx;
}

let logId = 0;

export function MqttProvider({ children }: { children: ReactNode }) {
  const [connState, setConnState] = useState<ConnState>("disconnected");
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [muted, setMuted] = useState<string[]>(DEFAULT_MUTED);
  const [settings, setSettings] = useState<ConnSettings>(loadSettings);

  const clientRef = useRef<mqtt.MqttClient | null>(null);
  const registryRef = useRef(new Map<string, Set<MsgHandler>>());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const pushLog = useCallback((dir: LogEntry["dir"], topic: string, text: string, size = 0) => {
    const entry: LogEntry = {
      id: ++logId,
      ts: Date.now(),
      dir,
      topic,
      preview: text.length > 160 ? text.slice(0, 160) + " …" : text,
      size,
    };
    setLog(prev => [entry, ...prev].slice(0, LOG_CAP));
  }, []);

  const connect = useCallback(
    () => {
      const settings = settingsRef.current;
      clientRef.current?.end(true);
      setConnState("connecting");
      setError(null);
      pushLog("sys", "", `连接 ${buildWsUrl(settings)}${settings.username ? `（用户 ${settings.username}）` : "（匿名）"}`);

      const c = mqtt.connect(buildWsUrl(settings), {
        clientId: `web-${Math.random().toString(16).slice(2, 10)}`,
        username: settings.username || undefined,
        password: settings.password || undefined,
        clean: true,
        reconnectPeriod: 0, // 测试工具手动控制连接
        connectTimeout: 5000,
        keepalive: 30,
      });
      clientRef.current = c;

      c.on("connect", () => {
        setConnState("connected");
        pushLog("sys", "", "已连接 broker");
        // 恢复所有已注册的订阅
        for (const pattern of registryRef.current.keys()) c.subscribe(pattern);
      });
      c.on("error", err => {
        const msg = err?.message ?? String(err);
        setError(msg);
        pushLog("sys", "", `错误：${msg}`);
        c.end(true);
      });
      c.on("close", () => {
        setConnState(s => (s === "connected" || s === "connecting" ? "disconnected" : s));
        pushLog("sys", "", "连接已关闭");
      });
      c.on("message", (topic: string, payload: Uint8Array) => {
        const text = new TextDecoder().decode(payload);
        if (!mutedRef.current.some(m => topic.includes(m))) {
          pushLog("in", topic, text, payload.length);
        }
        for (const [pattern, handlers] of registryRef.current) {
          if (topicMatches(pattern, topic)) {
            for (const h of handlers) h(topic, text, payload);
          }
        }
      });
    },
    [pushLog],
  );

  const disconnect = useCallback(() => {
    clientRef.current?.end(true);
    setConnState("disconnected");
  }, []);

  const subscribeTopic = useCallback((pattern: string, handler: MsgHandler) => {
    const reg = registryRef.current;
    let set = reg.get(pattern);
    if (!set) {
      set = new Set();
      reg.set(pattern, set);
      const c = clientRef.current;
      if (c?.connected) c.subscribe(pattern);
    }
    set.add(handler);
    return () => {
      const s = reg.get(pattern);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) {
        reg.delete(pattern);
        const c = clientRef.current;
        if (c?.connected) c.unsubscribe(pattern);
      }
    };
  }, []);

  const publishMsg = useCallback(
    (topic: string, payload: string, qos: 0 | 1 | 2 = 0, retain = false) => {
      const c = clientRef.current;
      if (!c?.connected) return false;
      c.publish(topic, payload, { qos, retain });
      pushLog("out", topic, payload, payload.length);
      return true;
    },
    [pushLog],
  );

  const clearLog = useCallback(() => setLog([]), []);
  const toggleMute = useCallback((sub: string) => {
    setMuted(prev => (prev.includes(sub) ? prev.filter(m => m !== sub) : [...prev, sub]));
  }, []);

  // 挂载后用上次保存的参数自动连接（本地测试工具）
  useEffect(() => {
    connect();
  }, [connect]);

  return (
    <MqttContext.Provider
      value={{
        connState,
        error,
        settings,
        setSettings,
        connect,
        disconnect,
        subscribeTopic,
        publishMsg,
        log,
        clearLog,
        muted,
        toggleMute,
      }}
    >
      {children}
    </MqttContext.Provider>
  );
}

/** 声明式订阅：pattern 为 null 时暂不订阅；handler 变化不会重复订阅 */
export function useTopic(pattern: string | null, handler: MsgHandler) {
  const { subscribeTopic } = useMqtt();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!pattern) return;
    return subscribeTopic(pattern, (topic, payload, bytes) => handlerRef.current(topic, payload, bytes));
  }, [pattern, subscribeTopic]);
}
